import type {
  CreateMeasurement,
  MeasurementKind,
  MeasurementPoint,
  MeasurementResponse,
  MeasurementSettings,
} from '@nutians/shared/schemas';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../api.js';

export const MEASUREMENT_SETTINGS_KEY = ['measurements', 'settings'] as const;
export const measurementsKey = (type?: MeasurementKind) =>
  type ? (['measurements', 'list', type] as const) : (['measurements', 'list'] as const);

export function useMeasurementSettings() {
  return useQuery<MeasurementSettings>({
    queryKey: MEASUREMENT_SETTINGS_KEY,
    queryFn: async () => {
      const { data, error } = await api.GET('/me/measurements/settings', {});
      if (error || !data) throw new Error('GET /me/measurements/settings failed');
      return data as MeasurementSettings;
    },
    staleTime: 60_000,
    retry: false,
  });
}

export function useUpdateMeasurementSettings() {
  const qc = useQueryClient();
  return useMutation<MeasurementSettings, Error, boolean>({
    mutationFn: async (enabled) => {
      const { data, error } = await api.PATCH('/me/measurements/settings', {
        body: { enabled },
      });
      if (error || !data) throw new Error('PATCH /me/measurements/settings failed');
      return data as MeasurementSettings;
    },
    onSuccess: (result) => {
      qc.setQueryData(MEASUREMENT_SETTINGS_KEY, result);
    },
  });
}

export function useMeasurements(type?: MeasurementKind) {
  return useQuery<MeasurementResponse>({
    queryKey: measurementsKey(type),
    queryFn: async () => {
      const params = type ? { params: { query: { type } } } : {};
      const { data, error } = await api.GET('/me/measurements', params);
      if (error || !data) throw new Error('GET /me/measurements failed');
      return data as MeasurementResponse;
    },
    staleTime: 30_000,
    retry: false,
  });
}

export function useCreateMeasurement() {
  const qc = useQueryClient();
  return useMutation<MeasurementPoint, Error, CreateMeasurement>({
    mutationFn: async (body) => {
      const { data, error } = await api.POST('/me/measurements', { body });
      if (error || !data) throw new Error('POST /me/measurements failed');
      return data as MeasurementPoint;
    },
    onSuccess: (point) => {
      void qc.invalidateQueries({ queryKey: measurementsKey(point.type) });
      void qc.invalidateQueries({ queryKey: measurementsKey() });
    },
  });
}

export function useDeleteMeasurement() {
  const qc = useQueryClient();
  return useMutation<void, Error, { id: string; type: MeasurementKind }>({
    mutationFn: async ({ id }) => {
      const { error } = await api.DELETE('/me/measurements/{id}', {
        params: { path: { id } },
      });
      if (error) throw new Error('DELETE /me/measurements failed');
    },
    onSuccess: (_, vars) => {
      void qc.invalidateQueries({ queryKey: measurementsKey(vars.type) });
      void qc.invalidateQueries({ queryKey: measurementsKey() });
    },
  });
}
